import type { StoreApi } from 'zustand'
import type { AppState } from '@/store/rootTypes'
import type { StudentWorkspace } from '@/entities/student-workspace'
import { activeFieldsFromWorkspace, activeWorkspacePatch, createEmptyWorkspace } from '@/store/workspace'
import { createLearningTaskCompletionKey } from '@/entities/progress'
import { createLearningTaskRewardClaimId } from '@/entities/reward-claim'
import { projectLearningAttemptPatch, resolveLearningAttemptReplay } from '@/store/slices/learningSlice'

type SetState = StoreApi<AppState>['setState']

type VocabMasteryUpdate = {
  unitId: string
  word: string
  status: 'new' | 'learning' | 'mastered'
}

function projectVocabMasteryUpdates(
  vocabMastery: AppState['vocabMastery'],
  updates: readonly VocabMasteryUpdate[],
): AppState['vocabMastery'] {
  return updates.reduce<AppState['vocabMastery']>((projected, update) => {
    const current = projected[update.unitId] || []
    const existingIndex = current.findIndex((item) => item.word === update.word)
    const next = existingIndex >= 0
      ? current.map((item, index) => index === existingIndex
          ? { ...item, status: update.status, reviewCount: item.reviewCount + 1 }
          : item)
      : [...current, { word: update.word, status: update.status, reviewCount: 0 }]
    return { ...projected, [update.unitId]: next }
  }, vocabMastery)
}

function projectLearningTaskCompletion(
  state: AppState,
  input: Parameters<AppState['completeLearningTask']>[0],
) {
  const completedAt = state.taskCompletedAt[input.completionId]
    ?? input.completedAt
    ?? new Date().toISOString()
  const duplicate = Boolean(state.completedTasks[input.completionId])
  const taskIds = [...new Set([input.completionId, ...input.taskIds])]
  const completedTasks = { ...state.completedTasks }
  const taskCompletedAt = { ...state.taskCompletedAt }
  for (const taskId of taskIds) {
    completedTasks[taskId] = true
    taskCompletedAt[taskId] ??= completedAt
  }
  const awardedStars = duplicate ? 0 : Math.max(0, input.rewardStars ?? 0)
  const stars = state.stars + awardedStars
  const student = awardedStars > 0
    ? { ...state.student, totalStars: state.student.totalStars + awardedStars }
    : state.student
  return {
    patch: {
      completedTasks,
      taskCompletedAt,
      stars,
      student,
      weeklyPlanActive: true,
      weeklyPlanStartDate: state.weeklyPlanStartDate ?? completedAt,
    },
    receipt: {
      status: duplicate ? 'duplicate' as const : 'completed' as const,
      completedAt,
      awardedStars,
    },
  }
}

function projectLearningRouteTaskCompletion(
  state: AppState,
  input: Parameters<AppState['completeLearningRouteTask']>[0],
) {
  if (state.activeStudentId !== input.studentId || !state.workspaces[input.studentId]) {
    throw new Error('Learning route completion must belong to the active student workspace.')
  }
  if (state.currentStage?.id !== input.stageId) {
    throw new Error('Learning route completion must belong to the active learning stage.')
  }
  const completionKey = createLearningTaskCompletionKey(input.stageId, input.taskId)
  const rewardClaimId = createLearningTaskRewardClaimId(input.studentId, input.stageId, input.taskId)
  const completedAt = state.taskCompletedAt[completionKey]
    ?? input.completedAt
    ?? new Date().toISOString()
  const duplicate = Boolean(state.completedTasks[completionKey])
    || state.rewardClaims.some((claim) => claim.id === rewardClaimId)
  if (duplicate) {
    return {
      patch: null,
      receipt: { status: 'duplicate' as const, completedAt, awardedStars: 0 },
    }
  }

  const completedTasks = { ...state.completedTasks, [completionKey]: true }
  const taskCompletedAt = { ...state.taskCompletedAt, [completionKey]: completedAt }
  for (const relatedTaskId of new Set(input.relatedTaskIds ?? [])) {
    if (relatedTaskId === input.taskId) continue
    completedTasks[relatedTaskId] = true
    taskCompletedAt[relatedTaskId] ??= completedAt
  }
  const awardedStars = Math.max(0, input.reward.stars)
  const stars = state.stars + awardedStars
  const student = awardedStars > 0
    ? { ...state.student, totalStars: state.student.totalStars + awardedStars }
    : state.student
  const rewardClaims = [...state.rewardClaims, {
    id: rewardClaimId,
    kind: 'learning-task' as const,
    status: 'pending' as const,
    studentId: input.studentId,
    stageId: input.stageId,
    unitId: input.unitId,
    taskId: input.taskId,
    stars: awardedStars,
    plantExperienceAwards: Object.fromEntries(
      Object.entries(input.reward.plantExperienceAwards)
        .map(([zone, amount]) => [zone, Math.max(0, amount ?? 0)]),
    ),
    starsGrantedAt: completedAt,
    createdAt: completedAt,
  }]
  return {
    patch: {
      completedTasks,
      taskCompletedAt,
      stars,
      student,
      rewardClaims,
      weeklyPlanActive: true,
      weeklyPlanStartDate: state.weeklyPlanStartDate ?? completedAt,
    },
    receipt: {
      status: 'completed' as const,
      completedAt,
      awardedStars,
      rewardClaimId,
    },
  }
}

export function createProgressSlice(set: SetState): Pick<AppState,
  'completeLearningTask' | 'completeLearningRouteTask' | 'completeLessonPlayer' | 'completeTask' | 'addStars' |
  'updateVocabMastery' | 'recordLessonPlayerAnswer' | 'activateWeeklyPlan' |
  'toggleWeeklyTask' | 'resetWeeklyPlan' | 'resetProgress'> {
  return {
    completeLearningTask: (input) => {
      let result: ReturnType<AppState['completeLearningTask']> | null = null
      set((state) => {
        const projection = projectLearningTaskCompletion(state, input)
        result = projection.receipt
        return { ...projection.patch, ...activeWorkspacePatch(state, projection.patch) }
      })
      if (!result) throw new Error('Learning task completion did not produce a receipt')
      return result
    },
    completeLearningRouteTask: (input) => {
      let result: ReturnType<AppState['completeLearningRouteTask']> | null = null
      set((state) => {
        const projection = projectLearningRouteTaskCompletion(state, input)
        result = projection.receipt
        if (!projection.patch) return state
        return { ...projection.patch, ...activeWorkspacePatch(state, projection.patch) }
      })
      if (!result) throw new Error('Learning route completion did not produce a receipt')
      return result
    },
    completeLessonPlayer: (input) => {
      let result: ReturnType<AppState['completeLessonPlayer']> | null = null
      set((state) => {
        const projection = input.completion.kind === 'route'
          ? projectLearningRouteTaskCompletion(state, input.completion.input)
          : projectLearningTaskCompletion(state, input.completion.input)
        result = projection.receipt
        if (projection.receipt.status === 'duplicate' || !projection.patch) return state
        if (input.completion.kind === 'route' && input.completion.input.unitId !== input.unitId) {
          throw new Error('Lesson player completion vocab must belong to the completed route unit.')
        }
        const vocabMastery = projectVocabMasteryUpdates(
          state.vocabMastery,
          input.vocabWords.map((word) => ({ unitId: input.unitId, word, status: 'learning' as const })),
        )
        const workspacePatch = { ...projection.patch, vocabMastery }
        return { ...workspacePatch, ...activeWorkspacePatch(state, workspacePatch) }
      })
      if (!result) throw new Error('Lesson player completion did not produce a receipt')
      return result
    },
    completeTask: (taskId) => set((state) => {
      if (state.completedTasks[taskId]) return state
      const completedAt = state.taskCompletedAt[taskId] ?? new Date().toISOString()
      const patch = {
        completedTasks: { ...state.completedTasks, [taskId]: true },
        taskCompletedAt: {
          ...state.taskCompletedAt,
          [taskId]: completedAt,
        },
        weeklyPlanActive: true,
        weeklyPlanStartDate: state.weeklyPlanStartDate ?? completedAt,
      }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    addStars: (amount) => set((state) => {
      const patch = {
        stars: state.stars + amount,
        student: { ...state.student, totalStars: state.student.totalStars + amount },
      }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    updateVocabMastery: (unitId, word, status) => set((state) => {
      const patch = { vocabMastery: projectVocabMasteryUpdates(state.vocabMastery, [{ unitId, word, status }]) }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    recordLessonPlayerAnswer: (input) => {
      let receipt: ReturnType<AppState['recordLessonPlayerAnswer']>
      set((state) => {
        if (input.attempt.source !== 'lesson-player') {
          throw new Error('Lesson player answers must use lesson-player evidence.')
        }
        if (input.mastery && (
          !input.attempt.correct
          || input.attempt.unitId !== input.mastery.unitId
        )) {
          throw new Error('Lesson player vocab mastery must belong to a correct answer in the same unit.')
        }
        const replay = resolveLearningAttemptReplay(state, input.attempt, 'Lesson player')
        if (replay.status === 'duplicate') {
          receipt = { status: 'duplicate' }
          return state
        }
        const attemptProjection = projectLearningAttemptPatch(state, replay.attempt)
        const vocabMastery = input.mastery
          ? projectVocabMasteryUpdates(state.vocabMastery, [input.mastery])
          : state.vocabMastery
        const workspacePatch = { ...attemptProjection.workspacePatch, vocabMastery }
        receipt = { status: 'recorded' }
        return {
          ...workspacePatch,
          pendingAchievements: attemptProjection.pendingAchievements,
          ...activeWorkspacePatch(state, workspacePatch),
        }
      })
      return receipt!
    },
    activateWeeklyPlan: () => set((state) => {
      const weeklyPlanStartDate = state.weeklyPlanStartDate ?? new Date().toISOString()
      const patch = { weeklyPlanActive: true, weeklyPlanStartDate, weeklyTaskDone: state.weeklyTaskDone }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    toggleWeeklyTask: (key) => set((state) => {
      const weeklyTaskDone = { ...state.weeklyTaskDone, [key]: !state.weeklyTaskDone[key] }
      const completionKey = `weekly:${key}`
      const taskCompletedAt = { ...state.taskCompletedAt }
      if (weeklyTaskDone[key]) taskCompletedAt[completionKey] = new Date().toISOString()
      else delete taskCompletedAt[completionKey]
      const patch = { weeklyTaskDone, taskCompletedAt }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    resetWeeklyPlan: () => set((state) => {
      const taskCompletedAt = Object.fromEntries(
        Object.entries(state.taskCompletedAt).filter(([key]) => !key.startsWith('weekly:')),
      )
      const patch = { weeklyPlanActive: false, weeklyPlanStartDate: null, weeklyTaskDone: {}, taskCompletedAt }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    resetProgress: () => set((state) => {
      if (!state.activeStudentId) return state
      const account = state.studentIndex.find((item) => item.id === state.activeStudentId)
      if (!account) return state
      const base = createEmptyWorkspace(account)
      const workspace: StudentWorkspace = {
        ...base,
        profile: state.profile,
        student: { ...base.student, name: state.student.name, grade: state.student.grade, avatar: state.student.avatar, layer: state.student.layer, style: state.student.style },
        currentGrade: state.currentGrade,
        pretestScore: state.pretestScore,
        pretestLayer: state.pretestLayer,
        learningStyle: state.learningStyle,
        learnerType: state.learnerType,
      }
      return { workspaces: { ...state.workspaces, [state.activeStudentId]: workspace }, ...activeFieldsFromWorkspace(workspace) }
    }),
  }
}
